import { ISelectMultiOption, Input, Modal } from '@salecycle/ui-component-library';
import { FC, useState } from 'react';
import * as RestApi from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import {
  IBlocklistAllowlistItem,
  ICreateBlocklistAllowListRequest,
  IListBlocklistAllowlistPostResponse,
  channelAll,
  channelEmail,
  listTypeAllowlist,
  listTypeBlocklist,
} from '../entities/blocklist-allowlist-constants';
import { useMutation } from 'react-query';
import { SiteSettingsApiEndpoints } from '../../utils/api-endpoints';
import { generatePath } from 'react-router';
import { useMessagePrettifier } from './hooks/useMessagePrettifier';
import { useUserDetails } from 'core/app/AppContext';
import { multiSelectOptionsToListOfStrings } from 'components/utils/getSelectOptions';

interface IFormProps {
  siteId: string;
  listType: string;
  valueType: string;
  itemName: string;
  itemValue: ISelectMultiOption<string>[];
  setInvalidItemValues: (value: Map<string, string[]>) => void;
  setDuplicateItemValues: (value: string[]) => void;
  commentModalOpen: boolean;
  setCommentModalOpen(isOpen: boolean): void;
  addBlocklistAllowlistCallback: (newItems: IBlocklistAllowlistItem[]) => void;
  resetItem(): void;
}

export type Props = IFormProps;

const BlocklistAllowlistCommentModal: FC<IFormProps> = props => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const { showNotification } = useNotifications();
  const { p } = useMessagePrettifier();
  const { username } = useUserDetails();
  const [commentTouched, setCommentTouched] = useState(comment.length !== 0);
  const closeHandler = () => {
    props.setCommentModalOpen(false);
    setComment('');
    setCommentTouched(false);
  };

  const handleCommentChange = (value: string) => {
    setComment(value);
    handleCommentTouch();
  };

  const handleCommentTouch = () => {
    if (!commentTouched) {
      setCommentTouched(true);
    }
  };

  const postBlocklistAllowlistMutation = useMutation(
    async (blocklistAllowlistItem: ICreateBlocklistAllowListRequest) => {
      return RestApi.post(
        generatePath(SiteSettingsApiEndpoints.POST_BLOCK_ALLOW_LIST_ITEM, {
          siteId: props.siteId,
        }),
        blocklistAllowlistItem,
      );
    },
    {
      onSuccess: data => {
        const newRows = data?.data as IListBlocklistAllowlistPostResponse;
        showNotification(p(props.valueType, props.listType, 'add', 'success'), 'normal');
        props.setInvalidItemValues(new Map<string, string[]>());
        props.setDuplicateItemValues([]);
        props.addBlocklistAllowlistCallback(newRows.data);
      },
      onError: () => {
        showNotification(p(props.valueType, props.listType, 'add', 'failure'), 'error');
      },
    },
  );

  const handleCreateBlocklistAllowlist = async (blocklistAllowlist: ICreateBlocklistAllowListRequest) => {
    postBlocklistAllowlistMutation.mutate(blocklistAllowlist);
  };

  const okHandler = async () => {
    let channelType = '';
    if (props.listType === listTypeAllowlist) {
      channelType = channelAll;
    }
    if (props.listType === listTypeBlocklist) {
      channelType = channelEmail;
    }
    await handleCreateBlocklistAllowlist({
      productType: channelType,
      listType: props.listType,
      valueType: props.valueType,
      values: multiSelectOptionsToListOfStrings(props.itemValue),
      name: props.itemName,
      updatedAt: Date.now().toString(),
      updatedById: username,
      comment: comment,
    } as ICreateBlocklistAllowListRequest);

    closeHandler();
    props.resetItem();
  };

  return (
    <Modal
      heading={t(`settings.${props.listType}.form.commentModal.header.${props.valueType}`)}
      variation="clean"
      isOpen={props.commentModalOpen}
      onCancelButtonClicked={closeHandler}
      onCloseButtonClicked={closeHandler}
      onOkButtonClicked={okHandler}
      okButtonLabel={t(`settings.${props.listType}.form.commentModal.okLabel.${props.valueType}`)}
      okButtonDisabled={!comment}
      cancelButtonLabel={t(`settings.${props.listType}.form.commentModal.cancelLabel`)}
    >
      <Input
        label={t(`settings.${props.listType}.form.commentModal.inputLabel`)}
        ariaLabel={t(`settings.${props.listType}.form.commentModal.ariaLabel`)}
        hint={t(`settings.${props.listType}.form.commentModal.hint`)}
        placeholder={t(`settings.${props.listType}.form.commentModal.placeholder`)}
        validationState={commentTouched && comment ? 'complete' : commentTouched ? 'warning' : null}
        validationMessage={
          !commentTouched
            ? null
            : commentTouched && !comment
            ? t(`settings.${props.listType}.form.commentModal.inputWarning`)
            : null
        }
        onChange={handleCommentChange}
        onBlur={handleCommentTouch}
        value={comment}
      />
    </Modal>
  );
};

export default BlocklistAllowlistCommentModal;
